import { atom } from 'jotai'
import { alphamapClient, brandsClient } from 'src/clients'
import { findPlace, findPlaceDetails, geocode } from 'src/utils/google-places'
import { SearchEntity, ExpandedEntity, SelectedEntity } from 'src/v2/types/core'
import { cloneDeep } from 'lodash'
import { SHOPPING_CENTER_NAICS_CODE } from 'src/constants/mapbox'
import { fullAddress } from 'src/utils/full-address'
import turf from 'src/utils/turf'
import { Position } from 'src/common-services'

/* 
  We go from searchEntity -> expandedEntity -> selectedEntity
  SearchEntity is whatever params as entry points we have
  ExpandedEntity is the result from using the available searchEntity data to get relevant data
  SelectedEntity is determining which type of entity we have selected and it's data
**/

// Search entity atom that is a catch-all for all 'entry point' data
export const searchEntity = atom<SearchEntity>({
  ids: {
    'am-loc': undefined,
    google: undefined,
    'am-brand': undefined,
    'am-opa': undefined,
    'am-oen': undefined,
    // more ids can be added here
  },
  geometry: undefined,
  address: undefined,
})

// Expanded entity based on what data we have available from the search entity
export const expandedEntity = atom(async (get) => {
  const sEntity = get(searchEntity)
  const expanded: ExpandedEntity = cloneDeep({
    ...sEntity,
    providers: {},
  })

  // Early return if we have a geometry this isn't fleshed out as yet
  if (expanded.geometry) {
    return expanded
  }

  // Expand brand info from search entity brand id or alphamap location brand id
  const brandId = expanded.ids['am-brand'] || expanded.providers['am-loc']?.brandId
  if (brandId) {
    try {
      const brand = await brandsClient.getAlphamapBrandByBrandId(brandId)
      expanded.providers['am-brand'] = brand
    } catch (e) {
      console.error('Error getting brand', e)
    }
    return expanded
  }

  // Expand as alphamap location id to get alphamap location
  if (expanded.ids['am-loc'] && !expanded.providers['am-loc']) {
    const location = await alphamapClient.locations.getById({
      id: expanded.ids['am-loc'],
    })
    expanded.providers['am-loc'] = location

    // Get google place details for this location
    const center = location.latLng.coordinates
    // We may have to try with name+address, name, or address
    const maybePlace =
      (await findPlace(`${location.name} ${fullAddress(location)}`, center)) ||
      (await findPlace(location.name, center)) ||
      (await findPlace(`${fullAddress(location)}`, center))
    const place = maybePlace ? await findPlaceDetails(maybePlace.place_id) : undefined
    expanded.providers.google = place || undefined
    return expanded
  }

  // Expand as google place id to get google place
  if (expanded.ids.google && !expanded.providers.google) {
    // Find google place details
    const googlePlace = await findPlaceDetails(expanded.ids.google)
    expanded.providers.google = googlePlace === null ? undefined : googlePlace
    return expanded
  }

  // Expand address to get google place
  if (expanded.address) {
    // try geocoding the address
    const geocoded = await geocode({
      address: expanded.address,
      language: 'en',
    })
    if (geocoded.length) {
      const place = geocoded[0]
      const googlePlace = (await findPlaceDetails(place.place_id)) || undefined
      expanded.providers.google = googlePlace
    }
    return expanded
  }

  // Expand as alphamap parcel id to get alphamap parcel
  if (expanded.ids['am-opa'] && !expanded.providers['am-opa']) {
    const parcel = await alphamapClient.ownership.getParcelById(expanded.ids['am-opa'])
    expanded.providers['am-opa'] = parcel
    return expanded
  }

  // Expand as alphamap owner id to get alphamap owner
  if (expanded.ids['am-oen'] && !expanded.providers['am-oen']) {
    const owner = await alphamapClient.ownership.getEntityPreview(expanded.ids['am-oen'])
    expanded.providers['am-oen'] = owner
    return expanded
  }

  return expanded
})

export const selectedEntity = atom<Promise<SelectedEntity | undefined>>(async (get) => {
  const expanded = await get(expandedEntity)

  if (expanded.geometry) {
    return {
      type: 'SELECTED_AREA',
      data: expanded.geometry,
    }
  }

  if (expanded.ids['am-brand'] && !!expanded.providers['am-brand']) {
    return {
      type: 'SELECTED_BRAND',
      data: expanded.providers['am-brand'],
    }
  }

  if (expanded.ids['am-loc'] && expanded.providers['am-loc'] && expanded.providers.google) {
    if (SHOPPING_CENTER_NAICS_CODE.includes(expanded.providers['am-loc'].naicsCode)) {
      return {
        type: 'SELECTED_SHOPPING_CENTER',
        data: {
          'am-loc': expanded.providers['am-loc'],
          google: expanded.providers.google,
        },
      }
    }
    return {
      type: 'SELECTED_LOCATION',
      data: {
        'am-loc': expanded.providers['am-loc'],
        google: expanded.providers.google,
      },
    }
  }

  if (expanded.ids.google && expanded.providers.google) {
    return {
      type: 'SELECTED_ADDRESS',
      data: expanded.providers.google,
    }
  }

  if (expanded.ids['am-opa'] && expanded.providers['am-opa']) {
    return {
      type: 'SELECTED_PARCEL',
      data: expanded.providers['am-opa'],
    }
  }

  if (expanded.ids['am-oen'] && expanded.providers['am-oen']) {
    return {
      type: 'SELECTED_OWNER',
      data: expanded.providers['am-oen'],
    }
  }

  // Nothing was selected
  return undefined
})

export const selectedEntityId = atom<Promise<string | undefined>>(async (get) => {
  const entity = await get(selectedEntity)
  if (!entity) {
    return undefined
  }
  switch (entity.type) {
    case 'SELECTED_LOCATION':
    case 'SELECTED_SHOPPING_CENTER':
      return entity.data['am-loc'].id
    case 'SELECTED_ADDRESS':
      return entity.data.place_id
    case 'SELECTED_BRAND':
    case 'SELECTED_PARCEL':
    case 'SELECTED_OWNER':
      return entity.data.id
    case 'SELECTED_AREA':
      return undefined
  }
})

export const selectedEntityLatLng = atom<Promise<Position | undefined>>(async (get) => {
  const entity = await get(selectedEntity)
  if (!entity) {
    return undefined
  }

  switch (entity.type) {
    case 'SELECTED_ADDRESS':
      return entity.data.geometry.location
    case 'SELECTED_LOCATION':
    case 'SELECTED_SHOPPING_CENTER':
      return entity.data['am-loc'].latLng.coordinates
    case 'SELECTED_PARCEL':
      return turf.centroid(entity.data.geometry).geometry.coordinates
    default:
      return undefined
  }
})
